import { PARTS } from "data/parts";
import { GRADES, UPGRADES } from "data/upgrades";
import {
  BattleRatingsData,
  CharacterBaseStats,
  CharacterDerivedStats,
  ExpandedUpgradeData,
  GradeModel,
  GroupedItemsData,
  InstalledUpgradesData,
  ItemData,
  ModifiedDerivedStats,
  PartModel,
  PartTypes,
  ShipTypes,
  ShipUpgradesModel,
  UpgradeComparisonData,
  UpgradeModel,
} from "types";
import { roundWhole } from "utils/formatters";
import {
  getMobData,
  getStarterPackUpgrades,
  getUnlockedMobUpgrades,
} from "./fight";
import { getDerivedStatData } from "./stats";

export const UPGRADE_BASE_CREDITS = 50;
export const RECALL_POSITION = 30;

// DATA

export const getItemData = (slug: string): ItemData => {
  const itemData = getUpgradeData(slug);

  return {
    slug: itemData.slug,
    name: itemData.name,
    description: itemData.description,
    icon: itemData.image,
  };
};

export const getAllUpgradesData = (): Record<string, UpgradeModel> => {
  return UPGRADES;
};

export const getUpgradeData = (slug: string): ExpandedUpgradeData => {
  const upgradeData = UPGRADES[slug];
  if (!upgradeData) {
    console.error(slug);
  }
  const gradeData = getGradeData(upgradeData.grade);

  // Calculate stats for upgrade
  const derivedStats = calculateUpgradeStats(upgradeData, gradeData);

  // Calculate sum of requirements for upgrade
  const requirementsSum = calculateRequirementsSum(upgradeData);

  return {
    ...upgradeData,
    gradeData,
    derivedStats,
    requirementsSum,
  };
};

export const getGradeData = (slug: string) => {
  return GRADES[slug];
};

// UPGRADE COMPARISONS

export const getUpgradeComparisonStats = (
  newUpgradeStats: ModifiedDerivedStats,
  installedUpgradeStats: ModifiedDerivedStats = {}
): UpgradeComparisonData => {
  const combinedStats = Object.keys({
    ...newUpgradeStats,
    ...installedUpgradeStats,
  });

  const statDiffs = combinedStats.reduce((acc, key) => {
    const derivedStat = key as keyof CharacterDerivedStats;

    const newStatValue = newUpgradeStats[derivedStat] || 0;
    const installedStatValue = installedUpgradeStats[derivedStat] || 0;
    const difference = newStatValue - installedStatValue;

    acc[derivedStat] = {
      difference,
      value: newStatValue,
    };
    return acc;
  }, {} as UpgradeComparisonData);

  return statDiffs;
};

export const getComparisonIndicator = (diffs: number[]) => {
  if (diffs.every((diff) => diff >= 0)) {
    return "⇑";
  } else if (diffs.every((diff) => diff <= 0)) {
    return "⇓";
  } else {
    return "⥤";
  }
};

// SHOP & INVENTORY

export const getUnlockedUpgrades = (
  ship: ShipTypes,
  battleRatings: BattleRatingsData
): string[] => {
  const unlockedUpgrades: string[] = [];

  // Include academy starter pack
  const starterPackUpgrades = getStarterPackUpgrades(ship);
  unlockedUpgrades.push(...starterPackUpgrades);

  for (const mobSlug in battleRatings) {
    // Get unlocked drops for each mob
    const mobData = getMobData(mobSlug);
    const previousBattleRating = battleRatings[mobSlug];
    const unlockedMobDrops = getUnlockedMobUpgrades(
      mobData.drops,
      previousBattleRating
    );

    // Dedupe upgrades by slug
    const uniqueUpgrades = unlockedMobDrops.filter(
      (upgrade) => !unlockedUpgrades.includes(upgrade)
    );
    unlockedUpgrades.push(...uniqueUpgrades);
  }

  return unlockedUpgrades;
};

export const getFilteredUpgrades = (
  upgrades: string[],
  statFilter: keyof CharacterDerivedStats | null
): string[] => {
  if (!!statFilter) {
    return upgrades.filter((upgrade) => {
      const { derivedStats } = getUpgradeData(upgrade);
      return !!derivedStats[statFilter];
    });
  }
  return upgrades;
};

export const getGroupedUpgrades = (
  upgrades: string[],
  storageUpgrades: string[]
): GroupedItemsData => {
  const groupedUpgrades = Object.entries(PARTS).reduce((acc, [key, value]) => {
    const partType = key as PartTypes;
    const partData = value as PartModel;
    const { name } = partData;
    return {
      ...acc,
      [partType]: {
        name,
        items: [],
      },
    };
  }, {} as GroupedItemsData);

  // Add storage group
  groupedUpgrades.storage = {
    name: "Storage",
    items: [],
  };

  // Group upgrades by their part property
  upgrades.forEach((upgrade) => {
    const { part } = getUpgradeData(upgrade);

    // Add to storage group if player has this upgrade in storage
    if (getInventoryUpgradeStorage(upgrade, storageUpgrades)) {
      groupedUpgrades.storage.items.push(upgrade);
    } else {
      groupedUpgrades[part].items.push(upgrade);
    }
  });

  // Sort upgrades within each group
  Object.keys(groupedUpgrades).forEach((key) => {
    const partType = key as PartTypes;
    groupedUpgrades[partType].items.sort((a, b) => {
      const aInfo = getUpgradeData(a);
      const bInfo = getUpgradeData(b);
      // Sort by credits - takes into account reqs and grade
      return (
        bInfo.requirementsSum - aInfo.requirementsSum ||
        bInfo.gradeData.multiplier - aInfo.gradeData.multiplier
      );
    });
  });

  return groupedUpgrades;
};

export const getInventoryUpgradeStorage = (
  slug: string,
  storageUpgrades: string[]
) => {
  if (storageUpgrades.includes(slug)) {
    return true;
  }

  return false;
};

export const calculateUpgradeStats = (
  upgrade: UpgradeModel,
  grade: GradeModel
): ModifiedDerivedStats => {
  const { requirements } = upgrade;
  const { multiplier } = grade;

  const derivedStats: { [key: string]: number } = {};

  const statMods = upgrade.derivedStatsModifiers;
  for (const key in statMods) {
    const statType = key as keyof CharacterDerivedStats;
    const statWeight = statMods[statType] as number;
    const statInfo = getDerivedStatData(statType);

    // Pick the requirement value based on corresponding base stat of derived stat
    const reqValue = requirements[statInfo.baseStat] || 0;

    const statValue = statInfo.rounder(
      statInfo.incrementValue * statWeight * reqValue * multiplier
    );

    derivedStats[statType] = statValue;
  }

  return derivedStats;
};

export const calculateUpgradeCredits = (
  upgrade: UpgradeModel,
  grade: GradeModel
): number => {
  const { multiplier } = grade;

  const requirementsSum = calculateRequirementsSum(upgrade);

  const credits = roundWhole(
    UPGRADE_BASE_CREDITS * requirementsSum * multiplier
  );

  return credits;
};

// UPGRADE REQUIREMENTS

export const calculateRequirementsSum = (upgrade: UpgradeModel): number => {
  const { requirements } = upgrade;

  const requirementsSum = Object.values(requirements).reduce(
    (acc, value) => acc + value,
    0
  );

  return requirementsSum;
};

export const getInvalidUpgradeParts = (
  installedUpgrades: InstalledUpgradesData | null,
  totalBaseStats: CharacterBaseStats
): PartTypes[] => {
  const invalidParts = [] as PartTypes[];

  // Ensure that character upgrade reqs are met by base stats
  for (const partKey in installedUpgrades) {
    const part = partKey as PartTypes;
    const upgradeSlug = installedUpgrades[part];
    if (!!upgradeSlug) {
      if (!areUpgradeRequirementsMet(upgradeSlug, totalBaseStats)) {
        invalidParts.push(part);
      }
    }
  }
  return invalidParts;
};

export const areUpgradeRequirementsMet = (
  upgradeSlug: string,
  totalBaseStats: CharacterBaseStats
) => {
  const { requirements } = getUpgradeData(upgradeSlug);

  // Check character base stat against upgrade reqs
  for (const key in requirements) {
    const baseStat = key as keyof CharacterBaseStats;
    const reqValue = requirements[baseStat] || 0;

    if (totalBaseStats[baseStat] < reqValue) {
      return false;
    }
  }
  return true;
};

export const getInstalledUpgradesByStatReq = (
  statSlug: keyof CharacterBaseStats,
  installedShipUpgrades: ShipUpgradesModel
): ExpandedUpgradeData[] => {
  // Get installed upgrades by stat req, sorted by stat
  let statReqUpgrades: ExpandedUpgradeData[] = [];

  for (const partKey in installedShipUpgrades) {
    const part = partKey as PartTypes;
    const shipUpgrade = installedShipUpgrades[part];

    const reqValue = shipUpgrade.requirements[statSlug] || 0;
    if (reqValue > 0) {
      statReqUpgrades.push(shipUpgrade);
    }
  }

  return statReqUpgrades;
};

export const getHighestInstalledUpgradeStatReq = (
  statSlug: keyof CharacterBaseStats,
  installedShipUpgrades: ShipUpgradesModel
): number => {
  const statReqUpgrades = getInstalledUpgradesByStatReq(
    statSlug,
    installedShipUpgrades
  );

  let highestReqValue = 0;

  for (const upgrade of statReqUpgrades) {
    const reqValue = upgrade.requirements[statSlug] || 0;
    if (reqValue > highestReqValue) {
      highestReqValue = reqValue;
    }
  }

  return highestReqValue;
};

// INSTALLED UPGRADES

export const getShipUpgradesData = (
  installedUpgrades?: InstalledUpgradesData
): ShipUpgradesModel => {
  installedUpgrades = installedUpgrades || {
    weapons: null,
    shields: null,
    thrusters: null,
    targetingSystem: null,
    reactorCore: null,
  };

  return {
    weapons: getUpgradeData(installedUpgrades.weapons || "default_weapons"),
    shields: getUpgradeData(installedUpgrades.shields || "default_shields"),
    thrusters: getUpgradeData(
      installedUpgrades.thrusters || "default_thrusters"
    ),
    targetingSystem: getUpgradeData(
      installedUpgrades.targetingSystem || "default_targeting_system"
    ),
    reactorCore: getUpgradeData(
      installedUpgrades.reactorCore || "default_reactor_core"
    ),
  };
};

export const getInstalledUpgradesStats = (
  installedUpgrades: InstalledUpgradesData | null,
  currentBaseStats: CharacterBaseStats,
  totalBaseStats: CharacterBaseStats
) => {
  const stats: CharacterDerivedStats = {
    maxHealth: 0,
    maxAttackDamage: 0,
    minAttackDamage: 0,
    attackSpeed: 0,
    attackAccuracy: 0,
    maxWeakenParts: 0,
    minWeakenParts: 0,
    damageReduction: 0,
    weakenPartsReduction: 0,
    attackEvasion: 0,
    movementSpeed: 0,
    fasterRecharge: 0,
    energyMultiplier: 0,
  };

  if (!!installedUpgrades) {
    for (const partKey in installedUpgrades) {
      const part = partKey as PartTypes;
      const upgradeSlug = installedUpgrades[part];
      if (!!upgradeSlug) {
        const statMods = getUpgradeData(upgradeSlug)
          .derivedStats as CharacterDerivedStats;
        for (const key in statMods) {
          const statType = key as keyof CharacterDerivedStats;
          const statInfo = getDerivedStatData(statType);

          // Use weakened parts to determine efficacy of upgrade stats
          const percentage =
            totalBaseStats[statInfo.baseStat] === 0
              ? 0 // If the total is somehow 0, no effect from upgrades
              : currentBaseStats[statInfo.baseStat] /
                totalBaseStats[statInfo.baseStat];
          stats[statType] += statInfo.rounder(statMods[statType] * percentage);
        }
      }
    }
  }

  return stats;
};
